/**
 * @file Business Calendar settings owner.
 */

import { tempConfigData } from "../../app/state/state.js";
import {
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    NATIVE_INPUT_CLASSES,
    bindNativeFieldBehavior,
} from "../../shared/forms/nativeFieldStyles.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import {
    destroyRegisteredControlsIn,
} from "../../shared/ui/controls/controlCore.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import { getLocalDateString } from "../../shared/utils/dateUtils.js";

const DEFAULT_TIMEZONE = "Pacific/Auckland";
const DEFAULT_ACTIVE_DAYS = Object.freeze([1, 2, 3, 4, 5]);
const COMMON_TIMEZONES = Object.freeze([
    "Pacific/Auckland",
    "Australia/Sydney",
    "Australia/Melbourne",
    "Australia/Brisbane",
    "Australia/Adelaide",
    "Australia/Perth",
    "UTC",
]);
const FALLBACK_TIMEZONES = Object.freeze([
    ...COMMON_TIMEZONES,
    "Pacific/Chatham",
    "Pacific/Fiji",
    "Pacific/Tongatapu",
    "America/Los_Angeles",
    "America/New_York",
    "Europe/London",
]);
const WEEKDAY_OPTIONS = Object.freeze([
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
    { value: 0, label: "Sunday" },
]);

let updateSaveButtonHandler = () => {};
let draftHolidayDate = "";
let editingHolidayId = "";
let showPastHolidays = false;

function attrValue(value = "") {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function isDateString(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
}

function formatStoredDate(value) {
    if (!isDateString(value)) {return "";}
    const [year, month, day] = String(value).split("-");
    return `${day}/${month}/${year}`;
}

function isPastHoliday(holiday) {
    return isDateString(holiday?.date) &&
        holiday.date < getLocalDateString();
}

function isValidTimeZone(value) {
    const timeZone = String(value || "").trim();
    if (!timeZone) {return false;}
    try {
        new Intl.DateTimeFormat("en-AU", { timeZone });
        return true;
    } catch {
        return false;
    }
}

function getSupportedTimeZones() {
    const supported = typeof Intl.supportedValuesOf === "function" ?
        Intl.supportedValuesOf("timeZone") :
        FALLBACK_TIMEZONES;
    return [...new Set([
        ...COMMON_TIMEZONES,
        ...supported,
    ])]
        .filter(isValidTimeZone)
        .sort((a, b) => a.localeCompare(b));
}

function formatTimeZoneLabel(timeZone) {
    return String(timeZone || "").replaceAll("_", " ");
}

function getTimeZoneOptions() {
    const allTimeZones = getSupportedTimeZones();
    const commonTimeZones = COMMON_TIMEZONES.filter((timeZone) =>
        allTimeZones.includes(timeZone));
    const remainingTimeZones = allTimeZones.filter((timeZone) =>
        !commonTimeZones.includes(timeZone));

    return [
        ...commonTimeZones.map((timeZone, index) => ({
            value: timeZone,
            label: formatTimeZoneLabel(timeZone),
            group: "Common",
            order: index,
        })),
        ...remainingTimeZones.map((timeZone, index) => ({
            value: timeZone,
            label: formatTimeZoneLabel(timeZone),
            group: "All timezones",
            order: COMMON_TIMEZONES.length + index,
        })),
    ];
}

function normalizeActiveDays(activeDays) {
    const source = Array.isArray(activeDays) ? activeDays : DEFAULT_ACTIVE_DAYS;
    return [...new Set(source
        .map((day) => Number(day))
        .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))]
        .sort((a, b) => a - b);
}

function normalizeHoliday(entry) {
    if (typeof entry === "string") {
        return {
            id: `holiday_${entry.replaceAll("-", "")}`,
            date: entry,
            name: "Holiday",
        };
    }
    const date = String(entry?.date || "");
    return {
        id: String(entry?.id || `holiday_${date.replaceAll("-", "")}`),
        date,
        name: String(entry?.name || "Holiday").trim() || "Holiday",
    };
}

function normalizeHolidays(holidays) {
    return (Array.isArray(holidays) ? holidays : [])
        .map(normalizeHoliday)
        .filter((holiday) => isDateString(holiday.date))
        .sort((a, b) => a.date.localeCompare(b.date));
}

function getVisibleHolidays(holidays) {
    return normalizeHolidays(holidays)
        .filter((holiday) => showPastHolidays || !isPastHoliday(holiday));
}

function ensureBusinessCalendarConfig() {
    const notificationSchedule = tempConfigData.notificationSchedule || {};
    if (!tempConfigData.businessCalendar ||
        typeof tempConfigData.businessCalendar !== "object") {
        tempConfigData.businessCalendar = {};
    }
    const businessCalendar = tempConfigData.businessCalendar;
    businessCalendar.timezone = String(
        businessCalendar.timezone ||
        notificationSchedule.timeZone ||
        DEFAULT_TIMEZONE,
    );
    businessCalendar.activeDays = normalizeActiveDays(
        Array.isArray(businessCalendar.activeDays) ?
            businessCalendar.activeDays :
            notificationSchedule.activeDays,
    );
    businessCalendar.holidays = normalizeHolidays(
        Array.isArray(businessCalendar.holidays) ?
            businessCalendar.holidays :
            notificationSchedule.holidays,
    );
    syncNotificationScheduleFromBusinessCalendar();
    return businessCalendar;
}

function syncNotificationScheduleFromBusinessCalendar() {
    const businessCalendar = tempConfigData.businessCalendar || {};
    tempConfigData.notificationSchedule = {
        ...(tempConfigData.notificationSchedule || {}),
        activeDays: normalizeActiveDays(businessCalendar.activeDays),
        holidays: normalizeHolidays(businessCalendar.holidays)
            .map((holiday) => holiday.date),
        timeZone: String(businessCalendar.timezone || DEFAULT_TIMEZONE),
    };
}

function renderBusinessDayCheckbox(day, activeDays) {
    const checked = activeDays.includes(day.value);
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES} rounded-md border border-gray-100 bg-gray-50 px-3 py-2" data-native-checkbox-label>
            <input
                type="checkbox"
                class="${NATIVE_CHECKBOX_CLASSES}"
                value="${day.value}"
                data-business-day-check
                ${checked ? "checked" : ""}
            >
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span>${escapeHTML(day.label)}</span>
        </label>
    `;
}

function renderShowPastHolidaysCheckbox() {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input
                type="checkbox"
                class="${NATIVE_CHECKBOX_CLASSES}"
                data-business-show-past-holidays
                ${showPastHolidays ? "checked" : ""}
            >
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span class="text-sm text-gray-600">Show past holidays</span>
        </label>
    `;
}

function renderHolidayRow(holiday, businessCalendar) {
    const index = businessCalendar.holidays.findIndex((entry) =>
        String(entry.id) === String(holiday.id));
    if (index < 0) {return "";}
    const isEditing = String(holiday.id) === String(editingHolidayId);
    return `
        <div class="grid gap-3 rounded-md border border-gray-100 bg-white px-3 py-2 md:grid-cols-[minmax(12rem,0.9fr)_minmax(0,1fr)_auto] md:items-center">
            ${isEditing ? `
                <div class="flex flex-col gap-1 sm:flex-row sm:items-center" data-business-holiday-date-field>
                    <span class="shrink-0 text-sm text-gray-500">Date</span>
                    <div class="min-w-0 flex-1" data-business-holiday-date-row="${index}"></div>
                </div>
                <div class="flex flex-col gap-1 sm:flex-row sm:items-center" data-business-holiday-name-field>
                    <label class="shrink-0 text-sm text-gray-500" for="business-holiday-name-${index}">
                        Name
                    </label>
                    <div class="min-w-0 flex-1">
                        <input
                            id="business-holiday-name-${index}"
                            type="text"
                            class="${NATIVE_INPUT_CLASSES}"
                            value="${attrValue(holiday.name || "Holiday")}"
                            data-business-holiday-name-row
                            data-index="${index}"
                        >
                    </div>
                </div>
            ` : `
                <div class="flex min-w-0 items-baseline gap-2" data-business-holiday-date-display>
                    <span class="shrink-0 text-sm text-gray-500">Date:</span>
                    <span class="min-w-0 truncate text-sm text-gray-900">
                        ${escapeHTML(formatStoredDate(holiday.date) || holiday.date)}
                    </span>
                </div>
                <div class="flex min-w-0 items-baseline gap-2" data-business-holiday-name-display>
                    <span class="shrink-0 text-sm text-gray-500">Name:</span>
                    <span class="min-w-0 truncate text-sm text-gray-900">
                        ${escapeHTML(holiday.name || "Holiday")}
                    </span>
                </div>
            `}
            <div class="flex items-center justify-start gap-1 md:justify-end">
                <button
                    type="button"
                    class="${isEditing ? "done-business-holiday-btn" : "edit-business-holiday-btn"} standard-action-icon-btn text-gray-400"
                    data-holiday-id="${attrValue(holiday.id)}"
                    title="${isEditing ? "Done editing holiday" : "Edit holiday"}"
                    aria-label="${isEditing ? "Done editing holiday" : `Edit ${attrValue(holiday.name || "holiday date")}`}"
                >
                    <span class="material-symbols-outlined text-[18px] leading-none">${isEditing ? "check" : "edit"}</span>
                </button>
                <button
                    type="button"
                    class="delete-business-holiday-btn standard-action-icon-btn text-gray-400 hover:text-red-600"
                    data-holiday-id="${attrValue(holiday.id)}"
                    title="Delete holiday date"
                    aria-label="Delete ${attrValue(holiday.name || "holiday date")}"
                >
                    <span class="material-symbols-outlined text-[18px] leading-none">delete</span>
                </button>
            </div>
        </div>
    `;
}

/**
 * Renders the admin Business Calendar settings tab.
 *
 * @param {HTMLElement} container Settings tab container.
 * @param {object} options Render options.
 * @param {Function} options.updateSaveButton Dirty-state callback.
 * @returns {void}
 */
export function renderBusinessCalendarSettings(container, { updateSaveButton } = {}) {
    updateSaveButtonHandler = updateSaveButton || (() => {});
    const businessCalendar = ensureBusinessCalendarConfig();
    destroyRegisteredControlsIn(container);
    container.innerHTML = `
        <div class="space-y-6">
            <section class="bg-white rounded-lg shadow-md p-6 space-y-5">
                <div>
                    <h3 class="text-lg font-semibold text-gray-900">Business timezone</h3>
                    <p class="mt-1 text-sm text-gray-500">
                        Scheduled notifications like due today and overdue are not sent on non-business days and holiday dates. Leave approval escalation uses the same Business Calendar.
                    </p>
                </div>
                <div class="max-w-md space-y-1">
                    <label class="block text-sm font-medium text-gray-700" for="business-calendar-timezone">
                        Scheduling timezone
                    </label>
                    <div data-business-calendar-timezone-control></div>
                </div>
                <div>
                    <h3 class="text-lg font-semibold text-gray-900">Business days</h3>
                    <h4 class="mb-3 mt-4 text-sm font-semibold text-gray-700">Work days</h4>
                    <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
                        ${WEEKDAY_OPTIONS.map((day) =>
        renderBusinessDayCheckbox(day, businessCalendar.activeDays)).join("")}
                    </div>
                </div>
            </section>
            <section class="bg-white rounded-lg shadow-md p-6 space-y-4">
                <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                    <h4 class="text-sm font-semibold text-gray-700">Holidays</h4>
                    <p class="mt-1 text-sm text-gray-500">
                        Holiday dates appear on the shared calendar and are skipped by Business Calendar scheduling.
                    </p>
                    </div>
                    ${renderShowPastHolidaysCheckbox()}
                </div>
                <div class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] md:items-end">
                    <div class="space-y-1">
                        <div data-business-holiday-date-control></div>
                    </div>
                    <div class="space-y-1">
                        <label class="block text-sm font-medium text-gray-700" for="business-calendar-holiday-name">
                            Name
                        </label>
                        <input
                            id="business-calendar-holiday-name"
                            type="text"
                            class="${NATIVE_INPUT_CLASSES}"
                            placeholder="Holiday"
                            data-business-holiday-name
                        >
                    </div>
                    <button
                        type="button"
                        class="add-business-holiday-btn settings-secondary-row-btn min-h-10 px-4 sm:min-h-[30px]"
                    >
                        Add
                    </button>
                </div>
                <div class="max-h-72 space-y-2 overflow-y-auto rounded-md border border-gray-100 bg-gray-50 p-2">
                    ${getVisibleHolidays(businessCalendar.holidays)
        .map((holiday) => renderHolidayRow(holiday, businessCalendar))
        .join("") || `
                        <p class="px-3 py-6 text-center text-sm text-gray-500">
                            No holiday dates configured.
                        </p>
                    `}
                </div>
            </section>
        </div>
    `;
    bindNativeFieldBehavior(container);
    mountBusinessCalendarControls(container);
}

function mountBusinessCalendarControls(container) {
    const businessCalendar = ensureBusinessCalendarConfig();
    const timeZoneRoot = container.querySelector(
        "[data-business-calendar-timezone-control]",
    );
    if (!timeZoneRoot) {return;}
    const selectedTimeZone = isValidTimeZone(businessCalendar.timezone) ?
        businessCalendar.timezone :
        DEFAULT_TIMEZONE;
    businessCalendar.timezone = selectedTimeZone;
    syncNotificationScheduleFromBusinessCalendar();
    createSelectControl({
        root: timeZoneRoot,
        accessibleName: "Scheduling timezone",
        value: selectedTimeZone,
        options: getTimeZoneOptions(),
        placeholder: "Select timezone",
        searchPlaceholder: "Search timezones",
        searchable: true,
        searchThreshold: 0,
        required: true,
        portalMenu: true,
        mirror: false,
        onChange: (value) => {
            businessCalendar.timezone = value || DEFAULT_TIMEZONE;
            syncNotificationScheduleFromBusinessCalendar();
            updateSaveButtonHandler(true);
        },
    });

    const holidayDateRoot = container.querySelector(
        "[data-business-holiday-date-control]",
    );
    if (holidayDateRoot) {
        createDateControl({
            root: holidayDateRoot,
            type: "date",
            value: draftHolidayDate,
            label: "Date",
            onChange: (value) => {
                draftHolidayDate = value || "";
            },
        });
    }

    container
        .querySelectorAll("[data-business-holiday-date-row]")
        .forEach((root) => {
            const index = Number.parseInt(root.dataset.businessHolidayDateRow, 10);
            const holiday = businessCalendar.holidays[index];
            if (!holiday) {return;}
            createDateControl({
                root,
                type: "date",
                value: holiday.date,
                label: "",
                onChange: (value) => {
                    holiday.date = value || "";
                    syncNotificationScheduleFromBusinessCalendar();
                    updateSaveButtonHandler(true);
                },
            });
        });
}

/**
 * Applies Business Calendar input changes.
 *
 * @param {HTMLElement} target Event target.
 * @returns {boolean} True when handled.
 */
export function handleBusinessCalendarSettingsChange(
    target,
    { rerenderCurrentTabContent = () => {} } = {},
) {
    const businessCalendar = ensureBusinessCalendarConfig();
    if (target?.matches?.("[data-business-show-past-holidays]")) {
        showPastHolidays = target.checked;
        rerenderCurrentTabContent();
        return true;
    }

    if (target?.matches?.("[data-business-holiday-name-row]")) {
        const index = Number.parseInt(target.dataset.index, 10);
        const holiday = businessCalendar.holidays[index];
        if (!holiday) {return true;}
        holiday.name = String(target.value || "").trim();
        syncNotificationScheduleFromBusinessCalendar();
        updateSaveButtonHandler(true);
        return true;
    }

    if (target?.matches?.("[data-business-day-check]")) {
        const day = Number(target.value);
        let activeDays = normalizeActiveDays(businessCalendar.activeDays);
        if (target.checked) {
            if (!activeDays.includes(day)) {activeDays.push(day);}
        } else {
            activeDays = activeDays.filter((entry) => entry !== day);
        }
        businessCalendar.activeDays = normalizeActiveDays(activeDays);
        syncNotificationScheduleFromBusinessCalendar();
        updateSaveButtonHandler(true);
        return true;
    }

    return false;
}

/**
 * Handles Business Calendar buttons.
 *
 * @param {HTMLElement} target Event target.
 * @param {object} options Handler dependencies.
 * @param {Function} options.rerenderCurrentTabContent Rerender callback.
 * @returns {boolean} True when handled.
 */
export function handleBusinessCalendarSettingsButtonClick(
    target,
    { rerenderCurrentTabContent = () => {} } = {},
) {
    const businessCalendar = ensureBusinessCalendarConfig();
    if (target?.closest?.(".add-business-holiday-btn")) {
        const nameInput = document.querySelector("[data-business-holiday-name]");
        const date = String(draftHolidayDate || "");
        const name = String(nameInput?.value || "Holiday").trim() || "Holiday";
        if (!isDateString(date)) {
            openConfirmationModal(
                "Business Calendar",
                "Select a valid holiday date.",
                () => {},
                false,
                "OK",
            );
            return true;
        }
        if (businessCalendar.holidays.some((holiday) => holiday.date === date)) {
            openConfirmationModal(
                "Business Calendar",
                "That holiday date is already configured.",
                () => {},
                false,
                "OK",
            );
            return true;
        }
        businessCalendar.holidays.push({
            id: `holiday_${date.replaceAll("-", "")}_${Date.now()}`,
            date,
            name,
        });
        businessCalendar.holidays = normalizeHolidays(businessCalendar.holidays);
        draftHolidayDate = "";
        editingHolidayId = "";
        syncNotificationScheduleFromBusinessCalendar();
        updateSaveButtonHandler(true);
        rerenderCurrentTabContent();
        return true;
    }

    const editButton = target?.closest?.(".edit-business-holiday-btn");
    if (editButton) {
        editingHolidayId = String(editButton.dataset.holidayId || "");
        rerenderCurrentTabContent();
        return true;
    }

    const doneButton = target?.closest?.(".done-business-holiday-btn");
    if (doneButton) {
        editingHolidayId = "";
        rerenderCurrentTabContent();
        return true;
    }

    const deleteButton = target?.closest?.(".delete-business-holiday-btn");
    if (deleteButton) {
        const holidayId = deleteButton.dataset.holidayId;
        businessCalendar.holidays = businessCalendar.holidays.filter((holiday) =>
            String(holiday.id) !== String(holidayId));
        if (String(editingHolidayId) === String(holidayId)) {
            editingHolidayId = "";
        }
        syncNotificationScheduleFromBusinessCalendar();
        updateSaveButtonHandler(true);
        rerenderCurrentTabContent();
        return true;
    }

    return false;
}

/**
 * Validates the Business Calendar settings draft before save.
 *
 * @returns {boolean} True when valid.
 */
export function validateBusinessCalendarSettingsDraft() {
    if (!tempConfigData.businessCalendar ||
        typeof tempConfigData.businessCalendar !== "object") {
        ensureBusinessCalendarConfig();
    }
    const businessCalendar = tempConfigData.businessCalendar;
    if (!isValidTimeZone(businessCalendar.timezone)) {
        openConfirmationModal(
            "Business Calendar",
            "Select a valid scheduling timezone.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    businessCalendar.activeDays = normalizeActiveDays(businessCalendar.activeDays);
    if (businessCalendar.activeDays.length === 0) {
        openConfirmationModal(
            "Business Calendar",
            "Select at least one business day.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    const invalidHoliday = (Array.isArray(businessCalendar.holidays) ?
        businessCalendar.holidays :
        [])
        .find((holiday) => !isDateString(holiday.date) ||
            !String(holiday.name || "").trim());
    if (invalidHoliday) {
        openConfirmationModal(
            "Business Calendar",
            "Every holiday date must have a valid date and name.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    const seenHolidayDates = new Set();
    const duplicateHoliday = normalizeHolidays(businessCalendar.holidays)
        .find((holiday) => {
            if (seenHolidayDates.has(holiday.date)) {return true;}
            seenHolidayDates.add(holiday.date);
            return false;
        });
    if (duplicateHoliday) {
        openConfirmationModal(
            "Business Calendar",
            "Holiday dates must be unique.",
            () => {},
            false,
            "OK",
        );
        return false;
    }

    businessCalendar.holidays = normalizeHolidays(businessCalendar.holidays);
    syncNotificationScheduleFromBusinessCalendar();
    return true;
}
